import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './Health.css';

const methodClass = (method) => `health-method health-method-${method.toLowerCase()}`;

const formatCheckedAt = (value) => {
  if (!value) return 'Not run yet';
  return new Date(value).toLocaleString();
};

const Health = () => {
  const { fetchWithAuth } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [target, setTarget] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(null);
  const [e2eResult, setE2eResult] = useState(null);
  const [e2eRunning, setE2eRunning] = useState(false);
  const [e2eError, setE2eError] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [scheduleRunning, setScheduleRunning] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const response = await fetchWithAuth('/api/health/mobile');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load mobile API catalog');
      setCatalog(data.endpoints || []);
      setTarget(data.target || null);
      setCatalogError(null);
    } catch (error) {
      setCatalogError(error.message);
    } finally {
      setCatalogLoading(false);
    }
  }, [fetchWithAuth]);

  const loadSchedule = useCallback(async () => {
    try {
      const response = await fetchWithAuth('/api/health/schedule');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load daily schedule');
      setSchedule(data);
      setScheduleError(null);
      if (data.lastResult?.steps) {
        setE2eResult((current) => {
          if (!current?.finishedAt) return data.lastResult;
          return new Date(data.lastResult.finishedAt) > new Date(current.finishedAt)
            ? data.lastResult
            : current;
        });
      }
    } catch (error) {
      setScheduleError(error.message);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    loadSchedule();
    const timer = setInterval(loadSchedule, 60000);
    return () => clearInterval(timer);
  }, [loadSchedule]);

  const runE2E = async () => {
    setE2eRunning(true);
    setE2eError(null);
    setE2eResult(null);
    try {
      const response = await fetchWithAuth('/api/health/e2e', { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to run mobile E2E health check');
      setE2eResult(data);
    } catch (error) {
      setE2eError(error.message);
    } finally {
      setE2eRunning(false);
    }
  };

  const runScheduledReport = async () => {
    setScheduleRunning(true);
    setScheduleError(null);
    try {
      const response = await fetchWithAuth('/api/health/schedule/run', { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to run the daily report');
      setE2eResult(data);
      await loadSchedule();
    } catch (error) {
      setScheduleError(error.message);
    } finally {
      setScheduleRunning(false);
    }
  };

  const checks = useMemo(() => {
    const resultByName = new Map((e2eResult?.steps || []).map((step) => [step.name, step]));
    const catalogNames = new Set(catalog.map((endpoint) => endpoint.name));
    const mainChecks = catalog.map((endpoint) => {
      const result = resultByName.get(endpoint.name);
      return {
        ...endpoint,
        status: result?.status || (e2eRunning ? 'checking' : 'not-run'),
        statusCode: result?.statusCode ?? null,
        responseTimeMs: result?.responseTimeMs ?? null,
        error: result?.error || null,
      };
    });
    const cleanupChecks = (e2eResult?.steps || [])
      .filter((step) => !catalogNames.has(step.name))
      .map((step) => ({ ...step }));
    return [...mainChecks, ...cleanupChecks];
  }, [catalog, e2eResult, e2eRunning]);

  const summary = useMemo(() => checks.reduce((result, check) => {
    result.total += 1;
    if (check.status === 'healthy') result.healthy += 1;
    else if (check.status === 'unhealthy') result.unhealthy += 1;
    else result.notRun += 1;
    return result;
  }, { total: 0, healthy: 0, unhealthy: 0, notRun: 0 }), [checks]);

  const isProdTarget = Boolean(target) && !/(^|\/\/)testapi\./i.test(target);
  const checksInProgress = e2eRunning || scheduleRunning;
  const allOperational = e2eResult?.status === 'healthy' && summary.unhealthy === 0;
  const overallText = checksInProgress
    ? 'Mobile API journey in progress'
    : !e2eResult
      ? 'Mobile API journey not run'
      : allOperational
        ? 'Mobile API is operational'
        : 'Mobile API needs attention';
  const overallClass = allOperational ? 'is-healthy' : e2eResult ? 'is-unhealthy' : 'is-pending';

  return (
    <div className="health-page">
      <div className="health-heading">
        <div>
          <h1>Mobile API Health</h1>
          <p>Real registration, login and CRUD checks against the API used by the mobile app.</p>
        </div>
        <button
          type="button"
          className="btn btn-primary health-run-button"
          onClick={runE2E}
          disabled={checksInProgress || catalogLoading || catalog.length === 0}
        >
          {checksInProgress ? 'Checking…' : 'Run all mobile checks'}
        </button>
      </div>

      {catalogError && <div className="error">Error: {catalogError}</div>}

      <div className={`health-overall ${overallClass}`}>
        <span className="health-overall-dot" />
        <div>
          <strong>{overallText}</strong>
          <span>Last journey: {formatCheckedAt(e2eResult?.finishedAt)}</span>
        </div>
      </div>

      <div className="health-summary-grid">
        <div className="health-summary-card">
          <span>Total mobile checks</span>
          <strong>{summary.total}</strong>
        </div>
        <div className="health-summary-card health-summary-success">
          <span>Passed</span>
          <strong>{summary.healthy}</strong>
        </div>
        <div className="health-summary-card health-summary-danger">
          <span>Failed</span>
          <strong>{summary.unhealthy}</strong>
        </div>
        <div className="health-summary-card health-summary-info">
          <span>Not run</span>
          <strong>{summary.notRun}</strong>
        </div>
      </div>

      <div className="health-services">
        <div className="health-service-card">
          <span className={`health-service-dot ${allOperational ? 'is-up' : e2eResult ? 'is-down' : 'is-pending'}`} />
          <div>
            <strong>Mobile API</strong>
            <span>{e2eResult ? e2eResult.status : 'Waiting for E2E run'} · {target || 'Loading target…'}</span>
          </div>
        </div>
        <div className="health-service-card">
          <span className={`health-service-dot ${e2eResult?.testAccount === 'deactivated' ? 'is-up' : e2eResult ? 'is-down' : 'is-pending'}`} />
          <div>
            <strong>Test data cleanup</strong>
            <span>{e2eResult?.testAccount || 'Not run yet'}</span>
          </div>
        </div>
      </div>

      <div className="card health-schedule-card">
        <div className="health-schedule-heading">
          <div>
            <div className="health-e2e-title-row">
              <h2 className="card-title">Daily mobile report</h2>
              <span className={`health-schedule-badge ${schedule?.configured && schedule?.enabled ? 'is-enabled' : ''}`}>
                {schedule?.enabled === false
                  ? 'DISABLED'
                  : schedule?.configured
                    ? 'ENABLED'
                    : 'SETUP REQUIRED'}
              </span>
            </div>
            <p>Runs the full mobile journey once per day and sends the result to Telegram.</p>
          </div>
          <button
            type="button"
            className="btn btn-primary health-e2e-button"
            onClick={runScheduledReport}
            disabled={checksInProgress || !schedule?.configured || !schedule?.enabled}
          >
            {scheduleRunning ? 'Running and sending…' : 'Run today now'}
          </button>
        </div>

        <div className="health-schedule-grid">
          <div>
            <span>Schedule</span>
            <strong>{schedule ? `${schedule.time} · ${schedule.timezone}` : 'Loading…'}</strong>
          </div>
          <div>
            <span>Next run</span>
            <strong>{schedule?.nextRunLocal || 'Waiting for Telegram settings'}</strong>
          </div>
          <div>
            <span>Channel</span>
            <strong>{schedule?.chatId || 'Not configured'}</strong>
          </div>
          <div>
            <span>Last report</span>
            <strong>
              {schedule?.lastResult
                ? `${schedule.lastResult.status} · Telegram ${schedule.lastResult.notificationStatus}`
                : 'Not run yet'}
            </strong>
          </div>
        </div>

        {!schedule?.configured && (
          <div className="health-e2e-warning">
            Set TELEGRAM_BOT_TOKEN and TELEGRAM_CHAT_ID in the server environment, then restart the admin server.
          </div>
        )}
        {schedule?.configured && schedule?.enabled && (
          <div className="health-schedule-note">
            “Run today now” performs the daily run immediately and counts as today’s single scheduled check.
          </div>
        )}
        {scheduleError && <div className="error health-e2e-error">Error: {scheduleError}</div>}
      </div>

      <div className="card health-e2e-card">
        <div className="health-e2e-heading">
          <div>
            <div className="health-e2e-title-row">
              <h2 className="card-title">Mobile user journey</h2>
              <span className={`health-environment-badge${isProdTarget ? ' is-prod' : ''}`}>
                {target ? (isProdTarget ? 'PROD API' : 'TEST API') : 'API'}
              </span>
            </div>
            <p>
              Registration, login and complete CRUD flows for foods, weights, measurements,
              fitness activities and fitness settings.
            </p>
          </div>
        </div>

        <div className="health-e2e-warning">
          {isProdTarget
            ? `Runs against ${target}. A unique temporary account is created on the live API, its records are cleaned up and the account is deactivated even if a step fails.`
            : 'A unique temporary account is created. Test records are cleaned up and the account is deactivated even if a step fails.'}
        </div>

        {e2eError && <div className="error health-e2e-error">Error: {e2eError}</div>}
        {e2eRunning && (
          <div className="health-e2e-progress">
            <span />
            Running the mobile steps sequentially. This can take up to a minute…
          </div>
        )}
        {e2eResult && (
          <div className={`health-e2e-result ${allOperational ? 'is-healthy' : 'is-unhealthy'}`}>
            <div>
              <strong>{allOperational ? 'Mobile journey passed' : 'Mobile journey has failed steps'}</strong>
              <span>{e2eResult.target} · {e2eResult.durationMs} ms</span>
            </div>
            <div className="health-e2e-counts">
              <span>{summary.healthy} passed</span>
              <span>{summary.unhealthy} failed</span>
            </div>
          </div>
        )}

        {catalogLoading ? (
          <div className="loading">Loading mobile endpoints…</div>
        ) : (
          <div className="table-container health-e2e-table-container">
            <table className="health-table health-e2e-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Mobile flow</th>
                  <th>Method</th>
                  <th>Endpoint</th>
                  <th>HTTP</th>
                  <th>Response</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {checks.map((check, index) => (
                  <tr key={`${check.name}-${index}`}>
                    <td>
                      <span className={`health-status health-status-${check.status}`}>
                        <span />
                        {check.status === 'not-run' ? 'Not run' : check.status}
                      </span>
                    </td>
                    <td>{check.name}</td>
                    <td><span className={methodClass(check.method)}>{check.method}</span></td>
                    <td><code>{check.path}</code></td>
                    <td>{check.statusCode ?? '—'}</td>
                    <td>{check.responseTimeMs == null ? '—' : `${check.responseTimeMs} ms`}</td>
                    <td className="health-details">{check.error || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="health-note">
        Only endpoints used by the mobile application are shown. KKAL Admin routes are intentionally excluded.
      </p>
    </div>
  );
};

export default Health;
